import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { ScrollReveal } from "../components/Reveal";

const sections = [
  {
    heading: "Who We Are",
    body: "The Visa Atelier is a UK-based visa and immigration administrative services provider, based in Milton Keynes. We assist clients with UK visas, OCI applications, and global visa support. For the purposes of this Privacy Policy, The Visa Atelier is the data controller responsible for your personal information.",
  },
  {
    heading: "Information We Collect",
    body: "We may collect your name, contact details, passport information, nationality, immigration history, supporting documents, financial information and other information required for the administrative services you request.",
  },
  {
    heading: "How We Collect Information",
    body: "Information is collected through consultations, email, WhatsApp, telephone, website forms, social media and documents you provide.",
  },
  {
    heading: "How We Use Information",
    body: "To prepare applications, organise documentation, communicate with you, book appointments, improve our services and comply with legal obligations.",
  },
  {
    heading: "Lawful Basis",
    body: "We process information under contract, legal obligation, legitimate interests and consent where applicable.",
  },
  {
    heading: "Sharing Information",
    body: "Information may be shared with government authorities, embassies, VFS, TLScontact and trusted service providers where necessary to deliver our services.",
  },
  {
    heading: "Data Security",
    body: "We use secure systems, password protection, restricted access and secure disposal of documents.",
  },
  {
    heading: "Retention",
    body: "Records are retained only as long as necessary to fulfil legal and business obligations.",
  },
  {
    heading: "Your Rights",
    body: "You may request access, correction, restriction, deletion where applicable, portability and withdrawal of consent.",
  },
  {
    heading: "Cookies",
    body: "Our website uses cookies to improve your browsing experience.",
  },
  {
    heading: "Marketing",
    body: "We only send marketing where you have opted in and you may unsubscribe at any time.",
  },
  {
    heading: "International Transfers",
    body: "Where required for international applications, data may be transferred outside the UK with appropriate safeguards.",
  },
  {
    heading: "Contact The Visa Atelier",
    body: null,
    list: [
      { k: "Email", v: "info@thevisaatelier.com" },
      { k: "Phone", v: "+44 7756 766759" },
      { k: "Website", v: "www.thevisaatelier.com" },
    ],
  },
];

export default function Privacy() {
  return (
    <div className="relative bg-navy min-h-screen" data-testid="privacy-page">
      <div className="grain" />
      <Nav />

      <main className="pt-32 md:pt-40">
        {/* Masthead */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
            <ScrollReveal className="col-span-12 md:col-span-8">
              <h1 className="font-display font-light text-ivory text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight">
                Privacy<br />
                <span className="italic text-gold">policy.</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal className="col-span-12 md:col-span-4" delay={0.15}>
              <div className="font-mono-ui text-xs tracking-[0.3em] uppercase text-ivory/60 mb-3">
                UK GDPR · Data Protection Act 2018
              </div>
              <p className="text-ivory/70 text-base md:text-lg leading-relaxed">
                This Privacy Policy explains how The Visa Atelier collects,
                uses, stores and protects your personal information in
                accordance with the UK GDPR and the Data Protection Act
                2018.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Policy sections */}
        <section className="bg-navy-800 border-y border-ivory/10 py-16 md:py-24">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 space-y-14 md:space-y-20">
            {sections.map((s, idx) => (
              <ScrollReveal key={s.heading} delay={idx * 0.03} y={20}>
                <article
                  className="grid grid-cols-12 gap-6 md:gap-10 items-start"
                  data-testid={`privacy-section-${idx}`}
                >
                  <div className="col-span-12 md:col-span-4">
                    <div className="font-mono-ui text-xs tracking-[0.28em] text-gold uppercase mb-3">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-ivory leading-tight tracking-tight">
                      {s.heading}
                    </h2>
                  </div>
                  <div className="col-span-12 md:col-span-8 border-l-0 md:border-l border-ivory/10 md:pl-8">
                    {s.body && (
                      <p className="text-ivory/85 text-base md:text-lg leading-relaxed">
                        {s.body}
                      </p>
                    )}
                    {s.list && (
                      <ul className="space-y-2">
                        {s.list.map((row) => (
                          <li
                            key={row.k}
                            className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-6 py-3 border-b border-ivory/10 last:border-0"
                          >
                            <span className="font-mono-ui text-xs tracking-[0.25em] uppercase text-gold min-w-[80px]">
                              {row.k}
                            </span>
                            <span className="text-ivory/85 text-base md:text-lg">
                              {row.v}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <div className="font-mono-ui text-xs tracking-[0.35em] text-gold uppercase mb-3">
                Questions about your data?
              </div>
              <h2 className="font-display font-light text-ivory text-4xl md:text-6xl leading-[0.95] tracking-tight">
                Reach out — we&rsquo;ll <span className="italic text-gold">respond.</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:info@thevisaatelier.com"
                className="inline-flex items-center gap-4 px-8 py-4 bg-gold text-navy font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-ivory transition-colors"
                data-testid="privacy-cta-email"
              >
                Email us →
              </a>
              <Link
                to="/faqs"
                className="inline-flex items-center gap-4 px-8 py-4 border border-gold text-gold font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-gold hover:text-navy transition-colors"
                data-testid="privacy-cta-faqs"
              >
                Read FAQs
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
